import {
    Button,
    Card,
    Collapse,
    Divider,
    Form,
    Input,
    Spin,
    Switch,
    Table,
} from 'antd';
import { useEffect, useState } from 'react';
import {
    useCreateUserFromScriptSureUserMutation,
    useGetOrganizationUsersQuery,
    useGetScriptSureUserQQuery,
    useGetUserQuery,
    useToggleUserStatusMutation,
} from '../../features/organization/organizationService';
import OrganizationCollapseView from '../../components/Organizations/OrganizationCollapseView';
import { RightOutlined, UnderlineOutlined } from '@ant-design/icons';
import { HospiceOrganizationWithDetailsResponse } from '../../models/OrganizationModels';
import Title from 'antd/lib/skeleton/Title';
import RoundedButton from '../../components/RoundedButton';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    ScriptSureUserWithDetailsDto,
    SimpleUserWithDetails,
    ToggleUserStatusRequest,
    UserWithDetailsDto,
} from '../../models/UserResponses';
import PageTitle from '../../components/PageTitle';
import OrganizationView from '../../components/Organizations/OrganizationView';
import { Utility } from '../../utilities/Utility';
import OrganizationUserImportDetails from './OrganizationUserImportDetails';
import ConfirmationModal from '../../components/Orders/ConfirmationModal';

const OrganizationUserUpdate = () => {
    const navigate = useNavigate();

    const { userId } = useParams();
    const uid = userId ?? '';

    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [isFailureModalOpen, setIsFailureModalOpen] = useState(false);

    const [createUser, { isLoading: isCreateUserLoading }] =
        useCreateUserFromScriptSureUserMutation();
    const [toggleUserStatus, { isLoading: isToggleLoading }] =
        useToggleUserStatusMutation();

    const {
        data: user,
        refetch: refetchUser,
        isLoading: isUserLoading,
    } = useGetUserQuery(uid);
    const { data: ssUser, isLoading: isSsUserLoading } =
        useGetScriptSureUserQQuery(user?.scriptSureUserId?.toString() ?? '');

    const postUser = async () => {
        try {
            if (!ssUser) return;

            const response: any = await createUser(ssUser);

            if (response.error) {
                setIsFailureModalOpen(true);
            } else {
                setIsSuccessModalOpen(true);
            }

            refetchUser();

        } catch (error) {
            setIsFailureModalOpen(true);
        }
    };

    const updateUserStatus = async () => {
        let reqBody: ToggleUserStatusRequest = {
            activateUser: !user!.isActive,
            userId: user!.id,
        };

        await toggleUserStatus(reqBody);
        refetchUser();
    };

    const lastUpdate = () => {
        if (Utility.isNullOrUndefinedOrEmpty(user?.lastScriptSureUpdate)) {
            return 'N/A';
        }
        
        const estDate = new Date(Utility.identifyDateAsUtc(user!.lastScriptSureUpdate!)).toLocaleString("en-US", { 
            timeZone: "America/New_York" 
        }); 
       
        return estDate;
    }

    return (
        <Spin
            spinning={
                isSsUserLoading ||
                isCreateUserLoading ||
                isUserLoading ||
                isToggleLoading
            }
        >
            <PageTitle title="User Information" onClick={() => navigate(-1)} />
            {user && (
                <div className="d-flex flex-column p-5 ms-5 shadow min-width-700">
                    <p className="fs-6 m-0 fw-semibold">Manage User</p>
                    <Divider className="mt-1 mb-4" />

                    <div className="d-flex flex-row align-items-center justify-content-between">
                        <div className="d-flex flex-row">
                            <p className="me-4">NaviScript User Status :</p>
                            <Switch
                                className="me-3"
                                checked={user.isActive}
                                onChange={updateUserStatus}
                            />
                            <p>{user.isActive ? 'Active' : 'Inactive'}</p>
                        </div>
                        <div className="d-flex flex-column gap-1">
                            <RoundedButton onClick={postUser} className="fw-semibold bg-light-blue text-white pt-2 pb-2 ps-4 pe-4 h-25 w-10 fs-6 ms-5 mb-1">Import User Updates</RoundedButton>
                            <p>{`Last Update : ${lastUpdate()}`}</p>
                        </div>
                    </div>
                </div>
            )}

            {ssUser && (
                <div className="mt-5 d-flex flex-column p-5 ms-5 shadow min-width-700">
                    <OrganizationUserImportDetails data={ssUser} />
                </div>
            )}

            <ConfirmationModal
                isOpen={isSuccessModalOpen || isFailureModalOpen}
                onClose={() =>
                    isSuccessModalOpen
                        ? setIsSuccessModalOpen(false)
                        : setIsFailureModalOpen(false)
                }
                isSuccess={isSuccessModalOpen}
                title={isSuccessModalOpen ? 'Successful!' : 'Unsuccessful!'}
                message={
                    isSuccessModalOpen
                        ? 'The user is successfully updated on NaviScript.'
                        : 'Failed to update the user on NaviScript.\n Please try again.'
                }
                primaryButtonText={
                    isSuccessModalOpen ? 'View User List' : 'Try Again'
                }
                onPrimaryButtonLinkToClick={
                    isSuccessModalOpen ? () => navigate(-1) : () => navigate(0)
                }
                secondaryButtonText=""
            />
        </Spin>
    );
};

export default OrganizationUserUpdate;
